import type { Environment, PlanktonState, RegionState, World } from '../types';
import { REGIONS, clockLatitudeDeg, region, type RegionId } from '../data/oceans';
import { REGION_WATER } from '../data/water/woa23';
import { REGION_LIGHT } from '../data/light/light';
import { SITES, type SiteId } from '../data/sites';
import { REGION_PLANKTON, SITE_PLANKTON } from '../data/plankton/places';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';
import { monthBlend, surfaceOn, type MonthBlend, type WaterClimatology } from '../waterColumn';
import type { LightClimatology } from '../light';
import { densityAtSurface } from '../seawater';
import { nextPlankton, type Nutrients, type PlanktonPlaceTable } from '../plankton';
import { chlorophyllOf } from '../chlorophyll';
import { exchangePlankton } from '../currents';
import { meanYearEnvironment } from './environment';

/**
 * Step 2 of the tick, production (M52): each place's plankton, a day on in
 * its environment (`plankton.ts`): every region's, then the currents'
 * exchange among them (M66, `currents.ts`), and every sanctuary's whose site
 * has water of its own (the aquarium's); a site that reads its region's
 * water reads its region's plankton. Nothing here draws.
 */

/** A place the plankton lives in: its sun's latitude, its water and light, and its table. */
export interface PlanktonPlace {
  latDeg: number;
  water: WaterClimatology;
  light: LightClimatology;
  table: PlanktonPlaceTable;
}

/** A region as the plankton's place. */
export function regionPlanktonPlace(id: RegionId): PlanktonPlace {
  return { latDeg: clockLatitudeDeg(region(id)), water: REGION_WATER[id], light: REGION_LIGHT[id], table: REGION_PLANKTON[id] };
}

/** A site as the plankton's place, or null where it reads its region's water. */
export function sitePlanktonPlace(id: SiteId): PlanktonPlace | null {
  const site = SITES[id];
  const table = SITE_PLANKTON[id];
  return site.water && site.light && table ? { latDeg: site.site.latDeg, water: site.water, light: site.light, table } : null;
}

/** The Atlas's nutrients on a day, and the density that turns them to the model's units, at the day's surface. */
export function nutrientsOn(water: WaterClimatology, blend: MonthBlend, env: Environment): Nutrients {
  return { water, blend, densityKgM3: densityAtSurface(surfaceOn(water, 'salinity', blend), env.seaSurfaceC) };
}

/** Each region's plankton and each aquarium's, a day on in the environment step 1 left, the regions' then carried by the currents. */
export function stepPlankton(world: World): void {
  const blend = monthBlend(epochDayOf(world));
  const stepped = {} as Record<RegionId, PlanktonState>;
  for (const r of world.regions) {
    const place = regionPlanktonPlace(r.id);
    stepped[r.id] = nextPlankton(r.plankton, r.env, nutrientsOn(place.water, blend, r.env), place.table.crossMixingMPerDay);
  }
  const carried = exchangePlankton(stepped);
  for (const r of world.regions) r.plankton = carried[r.id];
  for (const s of world.sanctuaries) {
    const place = sitePlanktonPlace(s.siteId);
    if (!s.plankton || !place) continue;
    s.plankton = nextPlankton(s.plankton, s.env, nutrientsOn(place.water, blend, s.env), place.table.crossMixingMPerDay);
  }
}

/**
 * A place's plankton on the day a world opens: the mean year's at the start
 * of the day's month (`PlanktonPlaceTable.monthStart`), stepped through the
 * mean year's days to the day (a definition: the record's departures, which
 * move only its temperature, are not followed for those few days).
 */
export function openingPlankton(place: PlanktonPlace, startEpochDay: number): PlanktonState {
  const { year, month } = civilFromEpochDay(startEpochDay);
  let p = place.table.monthStart[month - 1] as PlanktonState;
  for (let d = epochDayFromCivil(year, month, 1); d <= startEpochDay; d++) {
    const env = meanYearEnvironment(place.latDeg, place.water, place.light, d);
    p = nextPlankton(p, env, nutrientsOn(place.water, monthBlend(d), env), place.table.crossMixingMPerDay);
  }
  return p;
}

/**
 * The regions' plankton on the day a world opens (M66): each region's mean
 * year at the start of the day's month, stepped together through the mean
 * year's days to the day, the currents' exchange after each day's biology,
 * as the build spun them up (`scripts/plankton.ts`) and the tick steps them.
 */
export function openingRegionPlankton(startEpochDay: number): Record<RegionId, PlanktonState> {
  const { year, month } = civilFromEpochDay(startEpochDay);
  const places = REGIONS.map((r) => ({ id: r.id, place: regionPlanktonPlace(r.id) }));
  let states = Object.fromEntries(places.map(({ id, place }) => [id, place.table.monthStart[month - 1]])) as Record<RegionId, PlanktonState>;
  for (let d = epochDayFromCivil(year, month, 1); d <= startEpochDay; d++) {
    const blend = monthBlend(d);
    states = exchangePlankton(Object.fromEntries(places.map(({ id, place }) => {
      const env = meanYearEnvironment(place.latDeg, place.water, place.light, d);
      return [id, nextPlankton(states[id], env, nutrientsOn(place.water, blend, env), place.table.crossMixingMPerDay)];
    })) as Record<RegionId, PlanktonState>);
  }
  return states;
}

/** A sanctuary's plankton on the day a world opens, or null where its site reads its region's. */
export function sanctuaryOpeningPlankton(siteId: SiteId, startEpochDay: number): PlanktonState | null {
  const place = sitePlanktonPlace(siteId);
  return place ? openingPlankton(place, startEpochDay) : null;
}

/** The plankton a sanctuary's readout shows: its own, or its region's. */
export function sanctuaryPlankton(world: World, s: { plankton: PlanktonState | null; regionId: RegionId }): PlanktonState {
  return s.plankton ?? (world.regions.find((r) => r.id === s.regionId)?.plankton as PlanktonState);
}

/** The chlorophyll a sanctuary's readout shows, mg m⁻³ (M64): its own plankton's in its own water and light, or its region's in the region's. */
export function sanctuaryChlorophyll(world: World, s: { plankton: PlanktonState | null; siteId: SiteId; regionId: RegionId; env: Environment }): number {
  const blend = monthBlend(epochDayOf(world));
  const own = sitePlanktonPlace(s.siteId);
  if (s.plankton && own) return chlorophyllOf(s.plankton, s.env, nutrientsOn(own.water, blend, s.env).densityKgM3);
  const r = world.regions.find((x) => x.id === s.regionId) as RegionState;
  return chlorophyllOf(r.plankton, r.env, nutrientsOn(REGION_WATER[r.id], blend, r.env).densityKgM3);
}
